(() => {
  const config = window.msDeliveryConfig || { strings: {}, holidays: [] };
  const strings = config.strings;
  const text = (key, values = {}) =>
    Object.entries(values).reduce(
      (value, [name, replacement]) =>
        value.replaceAll(`[${name}]`, replacement),
      strings[key] || key,
    );
  const day = (year, month, date) => new Date(Date.UTC(year, month - 1, date));
  const iso = (date) => date.toISOString().slice(0, 10);
  const addDays = (date, count) => new Date(date.getTime() + count * 86400000);
  const zonedNow = (now) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-GB", {
        timeZone: "Europe/Zurich",
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hourCycle: "h23",
      })
        .formatToParts(now)
        .map((part) => [part.type, part.value]),
    );
    return {
      date: day(Number(parts.year), Number(parts.month), Number(parts.day)),
      hour: Number(parts.hour),
      minute: Number(parts.minute),
    };
  };
  const easter = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const value = h + l - 7 * m + 114;
    return day(year, Math.floor(value / 31), (value % 31) + 1);
  };
  const holiday = (date) => {
    const year = date.getUTCFullYear();
    const fixed = ["01-01", "08-01", "11-01", "12-25", "12-26"];
    const moving = [-2, 1, 39, 50].map((offset) =>
      iso(addDays(easter(year), offset)),
    );
    return (
      fixed.includes(iso(date).slice(5)) ||
      moving.includes(iso(date)) ||
      (config.holidays || []).includes(iso(date))
    );
  };
  const businessDay = (date, saturday = false) =>
    date.getUTCDay() !== 0 &&
    (saturday || date.getUTCDay() !== 6) &&
    !holiday(date);
  const nextBusinessDay = (date) => {
    let result = date;
    while (!businessDay(result)) result = addDays(result, 1);
    return result;
  };
  const addBusinessDays = (date, count, saturday = false) => {
    let result = date;
    let remaining = count;
    while (remaining > 0) {
      result = addDays(result, 1);
      if (businessDay(result, saturday)) remaining -= 1;
    }
    return result;
  };
  const normalize = (state) => {
    const local = Math.max(Number(state.local) || 0, 0);
    const supplier =
      state.status === "available"
        ? Math.max(Number(state.supplier) || 0, 0)
        : 0;
    const quantity = Math.max(Number(state.quantity) || 1, 1);
    const days = Number(state.days);
    const maxDays = Number(state.maxDays || state.days);
    const managed = state.managed !== false;
    const supplierNeeded = quantity > local && managed;
    const backorder = state.status === "backorder" && state.available !== false && Number.isFinite(days) && days > 0;
    const unavailable = managed
      ? quantity > local + supplier && !backorder
      : state.available === false;
    const known =
      !supplierNeeded || ((supplier > 0 || backorder) && Number.isFinite(days) && days > 0);
    return {
      ...state,
      local,
      supplier,
      quantity,
      managed,
      supplierNeeded,
      unavailable,
      known,
      minDays: supplierNeeded ? Math.ceil(days) : 0,
      maxDays: supplierNeeded
        ? Math.max(Math.ceil(days), Math.ceil(maxDays) || Math.ceil(days))
        : 0,
    };
  };
  const range = (states, method = "economy", now = new Date()) => {
    const normalized = states.map(normalize);
    if (
      !normalized.length ||
      normalized.some((state) => state.unavailable || !state.known)
    )
      return null;
    const current = zonedNow(now);
    const beforeCutoff =
      current.hour < 15 || (current.hour === 15 && current.minute === 0);
    const dispatch = nextBusinessDay(
      beforeCutoff ? current.date : addDays(current.date, 1),
    );
    const minDays = Math.max(...normalized.map((state) => state.minDays));
    const maxDays = Math.max(...normalized.map((state) => state.maxDays));
    const procure = (count) =>
      nextBusinessDay(
        config.procurementUnit === "business"
          ? addBusinessDays(dispatch, count)
          : addDays(dispatch, count),
      );
    const transit = {
      economy: [1, 2],
      priority: [1, 2],
      mondexpress: [1, 1],
      feldpost: [2, 3],
    }[method];
    if (method === "saturday") {
      const nextSaturday = (ready) => {
        let result = addDays(ready, 1);
        while (result.getUTCDay() !== 6 || holiday(result))
          result = addDays(result, 1);
        return result;
      };
      return {
        min: nextSaturday(procure(minDays)),
        max: nextSaturday(procure(maxDays)),
        dispatch,
        today: minDays === 0 && iso(dispatch) === iso(current.date),
      };
    }
    if (!transit) return null;
    return {
      min: addBusinessDays(
        procure(minDays),
        transit[0],
        method === "mondexpress",
      ),
      max: addBusinessDays(
        procure(maxDays),
        transit[1],
        method === "mondexpress",
      ),
      dispatch,
      today: minDays === 0 && iso(dispatch) === iso(current.date),
    };
  };
  const formatRange = (value) => {
    const crossesYear =
      value.min.getUTCFullYear() !== value.max.getUTCFullYear() ||
      value.min.getUTCFullYear() !== zonedNow(new Date()).date.getUTCFullYear();
    const formatter = new Intl.DateTimeFormat(
      document.documentElement.lang || "de-CH",
      {
        timeZone: "UTC",
        weekday: "short",
        day: "2-digit",
        month: "2-digit",
        ...(crossesYear ? { year: "numeric" } : {}),
      },
    );
    const first = formatter.format(value.min);
    return iso(value.min) === iso(value.max)
      ? first
      : `${first} – ${formatter.format(value.max)}`;
  };
  const restock = (raw, now = new Date()) => {
    const state = normalize(raw);
    if (
      !state.unavailable ||
      state.status !== "out_of_stock" ||
      state.local + state.supplier > 0 ||
      !/^\d{4}-\d{2}-\d{2}$/.test(state.restockDate || "")
    )
      return null;
    const [year, month, date] = state.restockDate.split("-").map(Number);
    const receipt = day(year, month, date);
    if (iso(receipt) !== state.restockDate) return null;
    const expected = addDays(receipt, Number(config.restockBufferDays) || 7);
    return expected >= zonedNow(now).date ? expected : null;
  };
  const leadTime = (state, key = "supplier") =>
    state.known
      ? text(key, {
          days:
            state.minDays === state.maxDays
              ? state.minDays
              : `${state.minDays}–${state.maxDays}`,
        })
      : text("supplier_unknown");
  const availability = (raw) => {
    const state = normalize(raw);
    if (state.unavailable)
      return state.local + state.supplier > 0
        ? text("quantity_error")
        : text("unavailable");
    if (state.supplierNeeded) return leadTime(state);
    if (!state.managed) return text("immediate");
    return state.local > 10
      ? text("local_many")
      : text("local", { count: state.local });
  };
  const render = (target, raw, options = {}) => {
    const state = normalize(raw);
    const method = options.method || "economy";
    const value = range(
      options.states || [raw],
      method,
      options.now || new Date(),
    );
    target.replaceChildren();
    const paragraph = (content, className = "") => {
      const element = document.createElement("p");
      element.className = `ms-delivery__line ${className}`;
      element.textContent = content;
      target.append(element);
    };
    const availabilityState = state.unavailable
      ? "unavailable"
      : state.supplierNeeded
        ? "supplier"
        : "local";
    if (!options.summary)
      paragraph(
        availability(raw),
        `ms-delivery__availability ms-delivery__availability--${availabilityState}`,
      );
    if (options.line) return;
    if (value) {
      paragraph(
        text("estimated", { date: formatRange(value) }),
        "ms-delivery__date",
      );
    } else {
      const expected =
        !options.summary && restock(raw, options.now || new Date());
      const date =
        expected &&
        new Intl.DateTimeFormat(document.documentElement.lang || "de-CH", {
          timeZone: "UTC",
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        }).format(expected);
      paragraph(
        expected ? text("restock", { date }) : text("unknown"),
        "ms-delivery__date",
      );
    }
    if (options.summary && value)
      paragraph(text("complete"), "ms-delivery__complete");
  };
  window.MilitarshopDelivery = {
    normalize,
    range,
    availability,
    render,
    restock,
    holiday,
    businessDay,
    zonedNow,
    iso,
    text,
  };
  if (!customElements.get("ms-delivery-estimate"))
    customElements.define(
      "ms-delivery-estimate",
      class extends HTMLElement {
        connectedCallback() {
          const orderData = this.querySelector("[data-order-states]");
          this.orderStates = orderData
            ? JSON.parse(orderData.textContent)
            : null;
          this.refresh = () => {
            const state = JSON.parse(this.dataset.state);
            const liveQuantity =
              this.dataset.product === "true" &&
              document.querySelector(
                '.js-product-form-main input[name="quantity"]',
              );
            if (liveQuantity) state.quantity = liveQuantity.value;
            render(
              this,
              state,
              this.dataset.line === "true"
                ? { line: true }
                : this.orderStates
                  ? { states: this.orderStates }
                  : {},
            );
          };
          this.refresh();
          if (this.dataset.product === "true") {
            document.addEventListener("input", this.refresh);
            document.addEventListener("change", this.refresh);
          }
          this.timer = setInterval(this.refresh, 60000);
        }
        disconnectedCallback() {
          clearInterval(this.timer);
          document.removeEventListener("input", this.refresh);
          document.removeEventListener("change", this.refresh);
        }
      },
    );
  if (!customElements.get("ms-order-delivery"))
    customElements.define(
      "ms-order-delivery",
      class extends HTMLElement {
        connectedCallback() {
          this.states = JSON.parse(this.querySelector("script").textContent);
          this.output = this.querySelector("[data-delivery-output]");
          this.select = this.querySelector("select");
          this.refresh = () =>
            render(this.output, this.states[0] || {}, {
              states: this.states,
              summary: true,
              method: this.select?.value || "economy",
            });
          this.select?.addEventListener("change", this.refresh);
          this.refresh();
          this.timer = setInterval(this.refresh, 60000);
        }
        disconnectedCallback() {
          clearInterval(this.timer);
        }
      },
    );
})();
